/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  JiraOauthCallbackParams,
  TLConnectionStatusResponse,
  TLConnectUrlResponse,
  TLDisconnectResponse,
  TLHTTPValidationError,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Jira Oauth Callback
 */
export const jiraOauthCallback = (
  params?: JiraOauthCallbackParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<unknown>({ url: `/mcp/jira/callback`, method: 'GET', params, signal }, options);
};

export const getJiraOauthCallbackQueryKey = (params?: JiraOauthCallbackParams) => {
  return ['mcp', 'jira', 'callback', ...(params ? [params] : [])] as const;
};

export const getJiraOauthCallbackQueryOptions = <
  TData = Awaited<ReturnType<typeof jiraOauthCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: JiraOauthCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraOauthCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getJiraOauthCallbackQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof jiraOauthCallback>>> = ({ signal }) =>
    jiraOauthCallback(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof jiraOauthCallback>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type JiraOauthCallbackQueryResult = NonNullable<Awaited<ReturnType<typeof jiraOauthCallback>>>;
export type JiraOauthCallbackQueryError = ErrorType<TLHTTPValidationError>;

export function useJiraOauthCallback<
  TData = Awaited<ReturnType<typeof jiraOauthCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | JiraOauthCallbackParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraOauthCallback>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraOauthCallback>>,
          TError,
          Awaited<ReturnType<typeof jiraOauthCallback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraOauthCallback<
  TData = Awaited<ReturnType<typeof jiraOauthCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: JiraOauthCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraOauthCallback>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraOauthCallback>>,
          TError,
          Awaited<ReturnType<typeof jiraOauthCallback>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraOauthCallback<
  TData = Awaited<ReturnType<typeof jiraOauthCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: JiraOauthCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraOauthCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Jira Oauth Callback
 */

export function useJiraOauthCallback<
  TData = Awaited<ReturnType<typeof jiraOauthCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: JiraOauthCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraOauthCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getJiraOauthCallbackQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Jira Oauth Callback
 */
export const prefetchJiraOauthCallbackQuery = async <
  TData = Awaited<ReturnType<typeof jiraOauthCallback>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: JiraOauthCallbackParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraOauthCallback>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getJiraOauthCallbackQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Jira Oauth Callback
 */
export const invalidateJiraOauthCallback = async (
  queryClient: QueryClient,
  params?: JiraOauthCallbackParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getJiraOauthCallbackQueryKey(params) }, options);

  return queryClient;
};

/**
 * @summary Jira Connect Url
 */
export const jiraConnectUrl = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLConnectUrlResponse>({ url: `/mcp/jira/connect-url`, method: 'POST', signal }, options);
};

export const getJiraConnectUrlQueryKey = () => {
  return ['POST', 'mcp', 'jira', 'connect-url'] as const;
};

export const getJiraConnectUrlQueryOptions = <
  TData = Awaited<ReturnType<typeof jiraConnectUrl>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectUrl>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getJiraConnectUrlQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof jiraConnectUrl>>> = ({ signal }) =>
    jiraConnectUrl(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof jiraConnectUrl>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type JiraConnectUrlQueryResult = NonNullable<Awaited<ReturnType<typeof jiraConnectUrl>>>;
export type JiraConnectUrlQueryError = ErrorType<unknown>;

export function useJiraConnectUrl<TData = Awaited<ReturnType<typeof jiraConnectUrl>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectUrl>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraConnectUrl>>,
          TError,
          Awaited<ReturnType<typeof jiraConnectUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraConnectUrl<TData = Awaited<ReturnType<typeof jiraConnectUrl>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectUrl>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraConnectUrl>>,
          TError,
          Awaited<ReturnType<typeof jiraConnectUrl>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraConnectUrl<TData = Awaited<ReturnType<typeof jiraConnectUrl>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Jira Connect Url
 */

export function useJiraConnectUrl<TData = Awaited<ReturnType<typeof jiraConnectUrl>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getJiraConnectUrlQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Jira Connect Url
 */
export const prefetchJiraConnectUrlQuery = async <
  TData = Awaited<ReturnType<typeof jiraConnectUrl>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectUrl>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getJiraConnectUrlQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Jira Connect Url
 */
export const invalidateJiraConnectUrl = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getJiraConnectUrlQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Jira Disconnect
 */
export const jiraDisconnect = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLDisconnectResponse>({ url: `/mcp/jira/disconnect`, method: 'DELETE', signal }, options);
};

export const getJiraDisconnectQueryKey = () => {
  return ['DELETE', 'mcp', 'jira', 'disconnect'] as const;
};

export const getJiraDisconnectQueryOptions = <
  TData = Awaited<ReturnType<typeof jiraDisconnect>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraDisconnect>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getJiraDisconnectQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof jiraDisconnect>>> = ({ signal }) =>
    jiraDisconnect(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof jiraDisconnect>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type JiraDisconnectQueryResult = NonNullable<Awaited<ReturnType<typeof jiraDisconnect>>>;
export type JiraDisconnectQueryError = ErrorType<unknown>;

export function useJiraDisconnect<TData = Awaited<ReturnType<typeof jiraDisconnect>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraDisconnect>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraDisconnect>>,
          TError,
          Awaited<ReturnType<typeof jiraDisconnect>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraDisconnect<TData = Awaited<ReturnType<typeof jiraDisconnect>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraDisconnect>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraDisconnect>>,
          TError,
          Awaited<ReturnType<typeof jiraDisconnect>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraDisconnect<TData = Awaited<ReturnType<typeof jiraDisconnect>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraDisconnect>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Jira Disconnect
 */

export function useJiraDisconnect<TData = Awaited<ReturnType<typeof jiraDisconnect>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraDisconnect>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getJiraDisconnectQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Jira Disconnect
 */
export const prefetchJiraDisconnectQuery = async <
  TData = Awaited<ReturnType<typeof jiraDisconnect>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraDisconnect>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getJiraDisconnectQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Jira Disconnect
 */
export const invalidateJiraDisconnect = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getJiraDisconnectQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Jira Connection Status
 */
export const jiraConnectionStatus = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLConnectionStatusResponse>({ url: `/mcp/jira/status`, method: 'GET', signal }, options);
};

export const getJiraConnectionStatusQueryKey = () => {
  return ['mcp', 'jira', 'status'] as const;
};

export const getJiraConnectionStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof jiraConnectionStatus>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectionStatus>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getJiraConnectionStatusQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof jiraConnectionStatus>>> = ({ signal }) =>
    jiraConnectionStatus(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof jiraConnectionStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type JiraConnectionStatusQueryResult = NonNullable<Awaited<ReturnType<typeof jiraConnectionStatus>>>;
export type JiraConnectionStatusQueryError = ErrorType<unknown>;

export function useJiraConnectionStatus<
  TData = Awaited<ReturnType<typeof jiraConnectionStatus>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectionStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraConnectionStatus>>,
          TError,
          Awaited<ReturnType<typeof jiraConnectionStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraConnectionStatus<
  TData = Awaited<ReturnType<typeof jiraConnectionStatus>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectionStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof jiraConnectionStatus>>,
          TError,
          Awaited<ReturnType<typeof jiraConnectionStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useJiraConnectionStatus<
  TData = Awaited<ReturnType<typeof jiraConnectionStatus>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Jira Connection Status
 */

export function useJiraConnectionStatus<
  TData = Awaited<ReturnType<typeof jiraConnectionStatus>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getJiraConnectionStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Jira Connection Status
 */
export const prefetchJiraConnectionStatusQuery = async <
  TData = Awaited<ReturnType<typeof jiraConnectionStatus>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof jiraConnectionStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getJiraConnectionStatusQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Jira Connection Status
 */
export const invalidateJiraConnectionStatus = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getJiraConnectionStatusQueryKey() }, options);

  return queryClient;
};
